import React, { useEffect, useState } from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import LoaderSpinner from "../../pageLoader/LoaderSpinner";
import DropdownInventory from "../../Modal/ModalInventory/DropdownInventory";
import ModalAddSubCategory from "../../Modal/ModalWebsite/ModalAddSubCategory";
import ModalAddCategory from "../../Modal/ModalWebsite/ModalAddCategory";
import ModalAddMainCategory from "../../Modal/ModalWebsite/ModalAddMainCategory";

import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";
import { GripVertical } from "lucide-react";
import ModalAddTeamMember from "../../Modal/ModalWebsite/ModalAddTeamMember";
import ModalAddTestimonial from "../../Modal/ModalWebsite/ModalAddTestimonial";

const DragHandle = SortableHandle(() => (
  <span className="drag-handle cursor-move">
    <GripVertical className="text-gray-500" />
  </span>
));

const SortableItem = SortableElement(({ product, handleDeleteEvent }) => (
  <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
    <td className="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2">
      <div className="flex items-center gap-2">
        <DragHandle />
        <Link to={`/dashboard/admin-testimonial/${product._id}`}>
          <div className="flex flex-row items-center gap-3">
            <img
              className="w-20 h-20 rounded-xl"
              src={product.singleImage}
              alt="Rounded avatar"
            />
            <h1 className="text-blue-500 hover:border-b hover:border-b-blue-500">
              {product.title}
            </h1>
          </div>
        </Link>
      </div>
    </td>
  
    <td className="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2">
       {product.description}
    </td>
    <td className="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2">
      {new Date(product.createdAt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })}
    </td>
  
    <td className="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2">
      <button onClick={() => handleDeleteEvent(product._id)}>delete</button>
    </td>
  </tr>
));

const SortableTableBody = SortableContainer(({ items, handleDeleteEvent }) => {
  if (!items || items.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan="5" className="text-center py-4 text-gray-500">
            No categories found
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {items.map((product, index) => (
        <SortableItem
          key={`item-${product._id}`}
          index={index}
          product={product}
          handleDeleteEvent={handleDeleteEvent}
          disabled={!product._id} // Disable sorting if no ID
        />
      ))}
    </tbody>
  );
});


const TestimonialAdmin = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [positionFilter, setPositionFilter] = useState(""); // State for tab filter
  const [phoneNumberFilter, setPhoneNumberFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedEventsData, setSelectedEventsData] = useState("");

  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [SelectedType, setSelectedType] = useState("");
  const [productCode, setProductCode] = useState("");
  const [title, setTitle] = useState("");

  const [isAddSubCategory, setIsAddSubCategory] = useState(false);

  const openModalAddSubCategory = () => {
    setIsAddSubCategory(true);
  };

  const closeModalAddSubCategory = () => {
    setIsAddSubCategory(false);
  };

  const handleButtonClick = (value) => {
    setSelectedType(value);
  };

  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);

      const page = searchParams.get("page") || 1;
      const dateFilter = searchParams.get("dateFilter") || "";
      const fromDate = searchParams.get("fromDate") || "";
      const toDate = searchParams.get("toDate") || "";
      const phoneNumber = searchParams.get("phoneNumber") || "";
      const selectedCategory = searchParams.get("selectedCategory") || "";
      const selectedEventsData = searchParams.get("selectedEventsData") || "";
      const SelectedType = searchParams.get("SelectedType") || "";
      const productCode = searchParams.get("productCode") || "";
      const title = searchParams.get("title") || "";

      const url = `https://server-gs.vercel.app/admin/testimonial?page=${page}&limit=5&dateFilter=${dateFilter}&fromDate=${fromDate}&toDate=${toDate}&phoneNumber=${phoneNumber}&selectedCategory=${selectedCategory}&selectedEventsData=${selectedEventsData}&SelectedType=${SelectedType}&productCode=${productCode}&title=${title}`;

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          setProducts(data.products || []);
          setTotalPages(data.totalPages || 0);
        })
        .catch((error) => console.error("Error fetching data:", error))
        .finally(() => setIsLoading(false));
    };

    // Fetch data on initial load
    fetchData();

    // Set up polling every 5 seconds
    const interval = setInterval(fetchData, 1000);

    return () => clearInterval(interval); // Clear interval on unmount
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchParams]);

  const currentPage = parseInt(searchParams.get("page")) || 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setSearchParams((prevParams) => {
        prevParams.set("page", nextPage);
        return prevParams;
      });
      setLoading(true);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setSearchParams((prevParams) => {
        prevParams.set("page", prevPage);
        return prevParams;
      });
      setLoading(true);
    }
  };

  const handleApplyFilters = () => {
    setSearchParams((prevParams) => {
      if (dateFilter) prevParams.set("dateFilter", dateFilter);
      if (fromDate) prevParams.set("fromDate", fromDate);
      if (toDate) prevParams.set("toDate", toDate);
      if (phoneNumberFilter) prevParams.set("phoneNumber", phoneNumberFilter);
      if (selectedCategory)
        prevParams.set("selectedCategory", selectedCategory);
      if (selectedEventsData)
        prevParams.set("selectedEventsData", selectedEventsData);
      if (SelectedType) prevParams.set("SelectedType", SelectedType);
      if (productCode) prevParams.set("productCode", productCode);
      if (title) prevParams.set("title", title);

      prevParams.set("page", 1);
      return prevParams;
    });
    setLoading(true);
  };

  const handleCancelFilters = () => {
    setDateFilter("");
    setFromDate("");
    setToDate("");
    setPhoneNumberFilter("");
    setSelectedCategory("");
    setSelectedEventsData("");
    setSelectedType("");
    setProductCode("");
    setTitle("");
    setSearchParams({ page: 1 });
  };

  const handleTabChange = (position) => {
    setPositionFilter(position);
    setSearchParams((prevParams) => {
      prevParams.set("position", position);
      prevParams.set("page", 1);
      return prevParams;
    });
    setLoading(true);
  };

  const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleDeleteEvent = async (productId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone. Do you really want to delete this category?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel",
      });

      // If the user cancels, exit the function
      if (!result.isConfirmed) {
        return;
      }

      await axios.delete(
        `https://server-gs.vercel.app/deleteTestimonial/${productId}`
      );

      // Show success message with auto-close
      Swal.fire({
        title: "Deleted!",
        text: "The category has been deleted successfully.",
        icon: "success",
        timer: 400, // Auto-close after 2 seconds
        timerProgressBar: true,
        showConfirmButton: false, // Hide "OK" button
      });

      // Optionally update UI without reloading
      // Example: setProducts(products.filter((product) => product._id !== productId));
    } catch (error) {
      console.error("Error deleting product:", error);
      Swal.fire(
        "Error",
        "Failed to delete the product. Please try again.",
        "error"
      );
    }
  };


  const onSortEnd = async ({ oldIndex, newIndex }) => {
    try {
      const updatedProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(updatedProducts);

      const response = await axios.put("https://server-gs.vercel.app/handle-sort-testimonial", {
        products: updatedProducts.map((p, i) => ({ _id: p._id, order: i + 1 }))
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Update error:", error);
      setProducts(products); // Revert on error
      Swal.fire("Error", error.message, "error");
    }
  };

  return (
    <div>
      {loading ? (
        <div className="loading-spinner">
          <LoaderSpinner />
        </div>
      ) : (
        <div className="flex flex-col">
          {/* Header Section */}
          <div className="w-full h-20 border-b border-gray-300 flex justify-between items-center px-4">
            <div className="flex flex-row gap-2 items-center">
              <button
                className="flex flex-row gap-2 items-center px-4 py-2 border text-lg text-gray-600 font-roboto  border-gray-300 rounded"
                onClick={toggleFilter}
              >
                {isFilterOpen ? (
                  <img
                    className="w-5 h-5"
                    alt=""
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHdpZHRoPSI1MTIiIGhlaWdodD0iNTEyIiB4PSIwIiB5PSIwIiB2aWV3Qm94PSIwIDAgMjggMjgiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDUxMiA1MTIiIHhtbDpzcGFjZT0icHJlc2VydmUiIGNsYXNzPSIiPjxnPjxzd2l0Y2g+PGc+PHBhdGggZmlsbD0iIzg1ODU4OSIgZD0ibTE1LjQgMTQgOC4zLTguM2MuNC0uNC40LTEgMC0xLjRzLTEtLjQtMS40IDBMMTQgMTIuNiA1LjcgNC4zYy0uNC0uNC0xLS40LTEuNCAwcy0uNCAxIDAgMS40bDguMyA4LjMtOC4zIDguM2MtLjQuNC0uNCAxIDAgMS40czEgLjQgMS40IDBsOC4zLTguMyA4LjMgOC4zYy40LjQgMSAuNCAxLjQgMHMuNC0xIDAtMS40eiIgb3BhY2l0eT0iMSIgZGF0YS1vcmlnaW5hbD0iIzg1ODU4OSIgY2xhc3M9IiI+PC9wYXRoPjwvZz48L3N3aXRjaD48L2c+PC9zdmc+"
                  />
                ) : (
                  <img
                    className="w-6 h-6"
                    alt=""
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHdpZHRoPSI1MTIiIGhlaWdodD0iNTEyIiB4PSIwIiB5PSIwIiB2aWV3Qm94PSIwIDAgMjQgMjQiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDUxMiA1MTIiIHhtbDpzcGFjZT0icHJlc2VydmUiPjxnPjxwYXRoIGQ9Ik0xOCAxM0g2Yy0uNiAwLTEtLjQtMS0xcy40LTEgMS0xaDEyYy42IDAgMSAuNCAxIDFzLS40IDEtMSAxek0xNSAxOUg5Yy0uNiAwLTEtLjQtMS0xcy40LTEgMS0xaDZjLjYgMCAxIC40IDEgMXMtLjQgMS0xIDF6TTIxIDdIM2MtLjYgMC0xLS40LTEtMXMuNC0xIDEtMWgxOGMuNiAwIDEgLjQgMSAxcy0uNCAxLTEgMXoiIGZpbGw9IiMwMDAwMDAiIG9wYWNpdHk9IjEiIGRhdGEtb3JpZ2luYWw9IiMwMDAwMDAiPjwvcGF0aD48L2c+PC9zdmc+"
                  />
                )}{" "}
                {isFilterOpen ? "Close" : "Filter"}
              </button>

              <h1 className="text-2xl  text-gray-500 font-roboto">Testimonial</h1>
            </div>

            <div className="">
              <button
                onClick={openModalAddSubCategory}
                className="px-6 py-2 border text-lg font-roboto bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-md hover:shadow-lg disabled:bg-blue-300 disabled:cursor-not-allowed"
                disabled={false} // Set this based on your logic
              >
                <div className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Add Testimonial
                </div>
              </button>
              <ModalAddTestimonial
              isVisible={isAddSubCategory}
              onClose={closeModalAddSubCategory}
              ></ModalAddTestimonial>

            </div>
          </div>

          <div className="flex flex-row w-full h-screen">
            {/* Sidebar */}
            <div
              className={`h-screen transition-all border-r border-gray-300 duration-300 ${
                isFilterOpen ? "w-[24%]" : "w-0"
              }`}
            >
              {isFilterOpen && (
                <>
                  {/* Category select */}

                  {/* <div className="p-5">
                    <label className="lg:text-md sm:text-sm md:text-sm font-roboto text-gray-700">
                      Sub Category Code
                    </label>
                    <input
                      type="text"
                      value={productCode}
                      onChange={(e) => setProductCode(e.target.value)}
                      className="border border-gray-300 text-gray-700 bg-white h-10 w-full rounded mt-2 p-2 text-sm font-roboto"
                      placeholder="Enter Product Code"
                    />
                  </div> */}
                  <div className="p-5">
                    <label className="lg:text-md sm:text-sm md:text-sm font-roboto text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="border border-gray-300 text-gray-700 bg-white h-10 w-full rounded mt-2 p-2 text-sm font-roboto"
                    />
                  </div>

                  {/* <div className="p-4">
                    <div className="flex lg:flex-row sm:flex-col md:flex-col gap-4">
                      <button
                        onClick={() => handleButtonClick("package")}
                        className={`px-4 py-2 border rounded font-roboto  text-md  ${
                          SelectedType === "package"
                            ? " text-black bg-gray-100 border-gray-700"
                            : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        Package
                      </button>
                      <button
                        onClick={() => handleButtonClick("product")}
                        className={`px-4 py-2 border rounded ${
                          SelectedType === "product"
                            ? " text-black bg-gray-100 border-gray-700"
                            : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        Product
                      </button>
                    </div>
                  </div> */}

                  <div className="flex lg:flex-row md:flex-col sm:flex-col gap-5 p-5">
                    <button
                      onClick={handleApplyFilters}
                      className="bg-blue-700 rounded text-white font-roboto text-md w-full h-10"
                    >
                      Apply Filters
                    </button>
                    <button
                      onClick={handleCancelFilters}
                      className="bg-gray-400 rounded text-white font-roboto text-md w-full h-10"
                    >
                      Cancel Filters
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Main Content */}
            <div
              className={`h-screen transition-all duration-300 ${
                isFilterOpen ? "lg:w-[80%]" : "w-full"
              }`}
            >
              <div className="border  border-gray-200  pb-4  w-full">
                <div class="relative  w-full">
                  <table class="w-full overflow-x-auto  text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                      <tr>
                        <th
                          scope="col"
                          class="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2 "
                        >
                          Full Name
                        </th>
                        <th
                          scope="col"
                          class="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2 "
                        >
                          Description
                        </th>

                     

                        <th
                          scope="col"
                          class="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2"
                        >
                          Upload date
                        </th>
                      
                        <th
                          scope="col"
                          class="lg:px-6 md:px-4 sm:px-2 lg:py-3 md:py-2 sm:py-2"
                        >
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <SortableTableBody
                      items={products}
                      onSortEnd={onSortEnd}
                      handleDeleteEvent={handleDeleteEvent}
                      useDragHandle
                      lockAxis="y"
                    />
                  </table>
                </div>

                <div>
                  <div class="flex my-4 items-center justify-center gap-5">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      class="flex items-center justify-center px-3 h-8 me-3 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                    >
                      <svg
                        class="w-3.5 h-3.5 me-2 rtl:rotate-180"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 10"
                      >
                        <path
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 5H1m0 0 4 4M1 5l4-4"
                        />
                      </svg>
                      Previous
                    </button>
                    <span className=" font-roboto font-semibold text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      class="flex items-center justify-center px-3 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                    >
                      Next
                      <svg
                        class="w-3.5 h-3.5 ms-2 rtl:rotate-180"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 10"
                      >
                        <path
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M1 5h12m0 0L9 1m4 4L9 9"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialAdmin;
